import { Loader } from "@/components/loader/Loader.tsx";
import { Markdown } from "@/components/markdown/Markdown.tsx";
import { eventDetailPath } from "@/components/calendars/calendar-url.ts";
import { useLocation } from "wouter";
import { getThreadPagePath } from "@/components/threads/util.ts";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs.tsx";
import { useContactProfilesEnabled } from "@/hooks/useContactProfilesEnabled.ts";
import { cn } from "@/lib/utils.ts";
import { BIO_BUILD_COPY, type BioBuildPhase } from "@/shared/contactProfile.ts";
import type { ContactCommunicationThread } from "@/types/contactActivity.ts";
import { useGateValue } from "@statsig/react-bindings";
import { format, formatDistanceToNow } from "date-fns";
import { Bug, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import { ContactCommunicationRow } from "./ContactCommunicationRow.tsx";
import { ContactEventRow } from "./ContactEventRow.tsx";
import {
  useBioBuild,
  useContactCommunications,
  useContactEvents,
  useContactMemories,
  useContactProfile,
} from "./useContactActivity.ts";

const ACTIVITY_TABS = ["bio", "communications", "events", "memories"] as const;

type ActivityTab = (typeof ACTIVITY_TABS)[number];

/** Radix reports the selected value as a bare string; only our own tabs may reach state. */
const isActivityTab = (tab: string): tab is ActivityTab => ACTIVITY_TABS.some((t) => t === tab);

type ContactActivityTabsProps = {
  contactId: string;
  /**
   * Which tab to open on. Defaults to Bio; a `sb_defaultTab` URL param can pin another.
   * Memories is reachable by click only, matching the sidebar schema.
   */
  defaultTab?: Exclude<ActivityTab, "memories">;
};

/**
 * The Communications + Events tabs shown under the contact card (BIG-5337).
 * Each tab fetches independently so a slow feed never blocks the card.
 *
 * Keyed by contact *and* requested tab so the selected tab resets to the default
 * whenever either changes — the sidebar swaps both props in place, without remounting
 * this subtree. Keying on the tab too is what lets a URL that pins one (`sb_defaultTab`,
 * restored by back/forward on the contact already open) override an earlier hand-picked
 * tab: the URL is the newer instruction, so it should win over the older click.
 */
export const ContactActivityTabs = (props: ContactActivityTabsProps) => (
  <ContactActivityTabsInner key={`${props.contactId}:${props.defaultTab ?? ""}`} {...props} />
);

/**
 * The strip itself, mounted per contact.
 *
 * Opens on Bio when the contact has a profile. A contact without one gets Bio as the
 * last tab, holding a button that builds one (BIG-9414), and the strip opens on
 * Communications instead. Once the user picks a tab by hand, that choice holds for as
 * long as the contact stays open.
 */
const ContactActivityTabsInner = ({ contactId, defaultTab }: ContactActivityTabsProps) => {
  const [, setLocation] = useLocation();
  // The Bio tab only exists for accounts in the contact-profiles rollout.
  const { enabled: bioEnabled } = useContactProfilesEnabled();
  const { data: profile, isLoading: isProfileLoading } = useContactProfile(contactId, bioEnabled);
  const hasProfile = profile != null;
  // The build is tracked here rather than in the Bio panel, because Radix unmounts an
  // inactive tab's panel, and the polling that lands the bio must keep running while
  // the user looks at another tab.
  const bioBuild = useBioBuild(contactId, bioEnabled && profile === null);

  const requestedTab = defaultTab ?? "bio";
  // Bio leads the strip once there's a profile. It also leads while the profile is still
  // in flight, so the panel opens on it rather than flashing Communications first. This
  // covers the profile query, not the gate query: while `bioEnabled` is still resolving,
  // the strip opens on Communications and swaps to Bio once the gate lands, since
  // holding Bio open through the gate would flash the tab at accounts outside the
  // rollout.
  const bioFirst = bioEnabled && (hasProfile || (requestedTab === "bio" && isProfileLoading));
  // A resolved null is the endpoint's 404, so the contact has no profile and Bio trails
  // the strip with its build button. An errored request leaves `profile` undefined and
  // shows no Bio at all, since nothing can be built or shown until it loads.
  const bioLast = bioEnabled && profile === null && !bioFirst;
  const [userTab, setUserTab] = useState<ActivityTab | null>(null);
  // A hand-picked tab wins over the default, and a late-arriving profile can't move the
  // user off it. Bio is only a default when there's a profile to show: a trailing Bio
  // opens only when picked by hand. And never hand Radix "bio" once Bio has taken itself
  // away, since a stale "bio" leaves the strip with nothing selected and nothing to
  // render.
  const desiredTab = userTab ?? requestedTab;
  const bioOpenable = bioFirst || (bioLast && userTab === "bio");
  const activeTab = desiredTab === "bio" && !bioOpenable ? "communications" : desiredTab;
  const bioTrigger = (
    <TabsTrigger variant="underline" size="underline" value="bio">
      Bio
    </TabsTrigger>
  );
  return (
    <Tabs
      value={activeTab}
      onValueChange={(tab) => {
        if (isActivityTab(tab)) setUserTab(tab);
      }}
      className="w-full"
    >
      <TabsList variant="underline" className="w-full justify-start overflow-x-auto">
        {bioFirst && bioTrigger}
        <TabsTrigger variant="underline" size="underline" value="communications">
          Communications
        </TabsTrigger>
        <TabsTrigger variant="underline" size="underline" value="events">
          Events
        </TabsTrigger>
        <TabsTrigger variant="underline" size="underline" value="memories">
          Memories
        </TabsTrigger>
        {bioLast && bioTrigger}
      </TabsList>

      {(bioFirst || bioLast) && (
        <TabsContent value="bio">
          <BioTab
            contactId={contactId}
            build={{
              phase: bioBuild.phase,
              isStarting: bioBuild.start.isPending,
              startFailed: bioBuild.start.isError,
              start: () => bioBuild.start.mutate(),
            }}
          />
        </TabsContent>
      )}

      {/* Both tabs open a row by emitting the entity's own canonical path and
          letting the surrounding boundary pick the surface: a card over this
          panel where the host carries a canvas, a drill onto the stack where
          this body is already a card on one, and the entity's own page on
          mobile. The row names what it opens; it never names where. */}
      <TabsContent value="communications">
        <CommunicationsTab
          contactId={contactId}
          // A row stands for a conversation, so it opens the conversation and
          // the reader picks the message they came for. How many messages that
          // is cannot be decided here: `messageCount` counts only the ones
          // involving *this contact*, while the conversation itself is the whole
          // exchange — so a contact copied on one message of a long thread would
          // look like a single message and the rest would be unreachable from
          // this row. The conversation view knows the real size once it loads,
          // and shows the message itself when there is only one.
          onOpen={(communicationId) => setLocation(`/communications/${communicationId}/thread`)}
        />
      </TabsContent>

      <TabsContent value="events">
        <EventsTab
          contactId={contactId}
          onOpen={(eventId) => setLocation(eventDetailPath(eventId))}
        />
      </TabsContent>

      <TabsContent value="memories">
        <MemoriesTab contactId={contactId} />
      </TabsContent>
    </Tabs>
  );
};

const EmptyState = ({ children }: { children: React.ReactNode }) => (
  <p className="px-2 py-6 text-center text-sm text-muted-foreground">{children}</p>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <h4 className="mb-1 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
    {children}
  </h4>
);

/** Keep the newest row per thread; a thread can straddle a keyset page boundary. */
const dedupeThreads = (threads: ContactCommunicationThread[]): ContactCommunicationThread[] => {
  const seen = new Set<string>();
  const result: ContactCommunicationThread[] = [];
  for (const thread of threads) {
    const key = thread.externalThreadId ?? thread.communicationId;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(thread);
  }
  return result;
};

const CommunicationsTab = ({
  contactId,
  onOpen,
}: {
  contactId: string;
  onOpen: (communicationId: string) => void;
}) => {
  const { data, isLoading, error, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useContactCommunications(contactId);

  if (isLoading) return <Loader />;
  if (error) return <EmptyState>Couldn't load communications.</EmptyState>;

  const threads = dedupeThreads(data?.pages.flatMap((page) => page.threads) ?? []);
  if (threads.length === 0) {
    return <EmptyState>No emails or texts with this contact yet.</EmptyState>;
  }

  return (
    <div className="flex flex-col">
      {threads.map((thread) => (
        <ContactCommunicationRow key={thread.communicationId} thread={thread} onOpen={onOpen} />
      ))}
      {hasNextPage && (
        <div className="flex justify-center pt-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            data-statsig-name="contact-communications-load-more"
          >
            {isFetchingNextPage ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Loading...
              </>
            ) : (
              "Load more"
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

const EventsTab = ({
  contactId,
  onOpen,
}: {
  contactId: string;
  onOpen: (eventId: string) => void;
}) => {
  const { data, isLoading, error } = useContactEvents(contactId);

  if (isLoading) return <Loader />;
  if (error) return <EmptyState>Couldn't load events.</EmptyState>;

  const recurring = data?.recurring ?? [];
  const oneOff = data?.oneOff ?? [];
  if (recurring.length === 0 && oneOff.length === 0) {
    return <EmptyState>No events with this contact yet.</EmptyState>;
  }

  return (
    <div className="flex flex-col gap-3">
      {recurring.length > 0 && (
        <section className="flex flex-col">
          <SectionHeader>Recurring</SectionHeader>
          {recurring.map((event) => (
            <ContactEventRow key={event.eventId} event={event} onOpen={onOpen} />
          ))}
        </section>
      )}
      {oneOff.length > 0 && (
        <section className="flex flex-col">
          {recurring.length > 0 && <SectionHeader>One-off</SectionHeader>}
          {oneOff.map((event) => (
            <ContactEventRow key={event.eventId} event={event} onOpen={onOpen} />
          ))}
        </section>
      )}
    </div>
  );
};

/** Capitalize the first letter for display (enum values are lowercased in the schema). */
const cap = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/** Best-effort absolute date; invalid/absent timestamps render nothing. */
const memoryDate = (iso: string): string | null => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : format(date, "PP");
};

/**
 * The Memories tab: the durable facts the assistant has saved about this contact,
 * newest first. Reads the dedicated `/contacts/:id/memories` endpoint so a slow
 * memory read never blocks the card.
 */
const MemoriesTab = ({ contactId }: { contactId: string }) => {
  const { data: memories, isLoading, error } = useContactMemories(contactId);

  if (isLoading) return <Loader />;
  if (error) return <EmptyState>Couldn't load memories.</EmptyState>;

  // The endpoint returns unordered rows and includes still-processing
  // (unconsolidated) memories whose content may be a placeholder — drop those and
  // show the durable facts newest-first.
  const visible = (memories ?? [])
    .filter((memory) => !memory.processing)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  if (visible.length === 0) {
    return <EmptyState>No memories saved for this contact yet.</EmptyState>;
  }

  return (
    <div className="flex flex-col divide-y">
      {visible.map((memory) => {
        const date = memoryDate(memory.date);
        const meta = [memory.category, date].filter(Boolean).join(" · ");
        return (
          <div key={memory.id} className="flex flex-col gap-1 px-2 py-2">
            <div className="flex items-start gap-2">
              <p className="min-w-0 flex-1 text-sm">{memory.content}</p>
              {!memory.confirmed && (
                <Badge variant="secondary" size="xs">
                  Unconfirmed
                </Badge>
              )}
            </div>
            {meta && <span className="text-xs text-muted-foreground">{meta}</span>}
          </div>
        );
      })}
    </div>
  );
};

/** Best-effort "researched 3 days ago"; absent/invalid timestamps just render nothing. */
const researchedAgo = (iso: string): string | null => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `Researched ${formatDistanceToNow(date, { addSuffix: true })}`;
};

/** What the Bio panel needs to show and start a build, owned by the tab strip. */
type BioBuild = {
  phase: BioBuildPhase;
  isStarting: boolean;
  startFailed: boolean;
  start: () => void;
};

/** The Bio panel for a contact with no profile: a button that builds one. */
const BioBuildPanel = ({ build }: { build: BioBuild }) => {
  if (build.phase === "building") {
    return (
      <div className="flex flex-col items-center gap-2 px-2 py-6 text-center">
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{BIO_BUILD_COPY.building}</p>
      </div>
    );
  }
  const slow = build.phase === "slow";
  return (
    <div className="flex flex-col items-center gap-3 px-2 py-6 text-center">
      <p className="text-sm text-muted-foreground">
        {slow ? BIO_BUILD_COPY.slow : BIO_BUILD_COPY.idle}
      </p>
      <Button
        size="sm"
        variant="outline"
        disabled={build.isStarting}
        onClick={build.start}
        data-statsig-name="contact-bio-build"
      >
        {build.isStarting && <Loader2 className="size-3 animate-spin" />}
        {slow ? BIO_BUILD_COPY.retry : BIO_BUILD_COPY.build}
      </Button>
      {build.startFailed && (
        <p className="text-xs text-destructive">{BIO_BUILD_COPY.startFailed}</p>
      )}
    </div>
  );
};

/**
 * The Bio tab: a contact's research-backed profile — a compact metadata header
 * (relationship, importance, temperature, role @ company) plus the markdown
 * `writeup` narrative. Read-only review surface; falls back to `summary` when the
 * long-form writeup is absent.
 */
const BioTab = ({ contactId, build }: { contactId: string; build: BioBuild }) => {
  const { data: profile, isLoading, error } = useContactProfile(contactId);
  // Call this before the early returns below so the hook count stays stable across renders.
  const isAdmin = useGateValue("internal-features");

  if (isLoading) return <Loader />;
  // A failed poll keeps the last good `null`, so check for a profile-less contact before
  // the error: a hiccup mid-build shouldn't swap the build panel for an error.
  if (profile === null) return <BioBuildPanel build={build} />;
  if (error || !profile) return <EmptyState>Couldn't load profile.</EmptyState>;

  // Labeled metadata chips — kept minimal for now (importance + temperature).
  const chips: { label: string; value: string }[] = [
    { label: "Importance", value: profile.importanceTier },
    { label: "Temperature", value: profile.temperature },
  ].flatMap((chip) => (chip.value ? [{ label: chip.label, value: chip.value }] : []));

  const roleLine = [profile.role, profile.companyName].filter(Boolean).join(" · ");
  const body = profile.writeup ?? profile.summary;
  const researched = researchedAgo(profile.researchUpdatedAt);

  return (
    <div className="flex flex-col gap-3 px-2 py-2">
      {(roleLine || profile.contactInfo?.location || chips.length > 0 || researched) && (
        <div className="flex flex-col gap-2">
          {(roleLine || profile.contactInfo?.location) && (
            <p className="text-sm text-muted-foreground">
              {[roleLine, profile.contactInfo?.location].filter(Boolean).join(" — ")}
            </p>
          )}
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {chips.map((chip) => (
                <Badge key={chip.label} variant="secondary" size="sm">
                  {chip.label}: {cap(chip.value)}
                </Badge>
              ))}
            </div>
          )}
          {researched && <p className="text-xs text-muted-foreground">{researched}</p>}
        </div>
      )}

      {body ? (
        <Markdown
          className={cn(
            "prose prose-sm prose-zinc max-w-none text-sm text-foreground",
            "prose-headings:mt-3 prose-headings:mb-1 prose-headings:font-semibold",
            "prose-h1:text-base prose-h2:text-sm prose-h3:text-xs",
            "prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5",
          )}
        >
          {body}
        </Markdown>
      ) : (
        <EmptyState>No write-up for this contact yet.</EmptyState>
      )}

      {/* Internal-only: a debug jump to the build thread that produced this profile. */}
      {isAdmin && profile.generatedByThreadId != null && (
        <div className="mt-1 flex flex-col gap-1.5 border-t border-dashed border-border pt-3">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground/60">
            Internal
          </span>
          <Button
            asChild
            variant="outline"
            size="2xs"
            className="w-fit border-dashed text-muted-foreground"
            data-statsig-name="contact-bio-debug-generating-thread"
          >
            <Link href={getThreadPagePath(profile.generatedByThreadId)}>
              <Bug className="size-3" />
              View generating thread
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};
